import { ART_PAL, fade, mix, n, rng, shade, tint, vgrad, warm, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Firecrackers scene behind the game zone: a festive red night (warm vermilion paper in light) — a gold glow behind the
 * grid, gold auspicious clouds (祥云) low at the sides, a string of firecrackers hanging from a short gold cord on the
 * left, two red lanterns on the right, gold sparks in the side bands and a few spent red paper curls at the bottom right.
 * Cords start below the title row and every motif stays in the free bands beside the grid (at 1440 × 900 about
 * x 214…460 and 1140…1386 of the scene); the bottom left stays clear for the status and the disclaimer. The centre
 * stays calm (only the glow). No characters (no 福), no text, no filters. Motifs are marked `data-motif="<x>"`.
 */
export function firecrackersScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `sf${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;
  const o = (dark: number, light: number) => n(P.dark ? dark : light);

  const top = P.dark ? mix(P.bg, P.red, 0.2) : mix(P.red, '#FFFFFF', 0.88);
  const mid = P.dark ? mix(P.bg, P.red, 0.12) : mix(P.gold, '#FFFFFF', 0.86);
  const bottom = P.dark ? mix(P.bg, P.red, 0.06) : mix(P.red, '#FFFFFF', 0.86);
  const gold = warm(P);
  const red = P.dark ? P.red : tint(P.red, 0.05);
  const redDeep = shade(P.red, P.dark ? 0.35 : 0.2);
  const cord = P.dark ? mix(P.gold, P.red, 0.4) : shade(P.gold, 0.1);
  const cloudFill = P.dark ? mix(P.bg, P.red, 0.26) : mix(P.gold, '#FFFFFF', 0.72);

  const defs =
    `<defs>${vgrad(`${p}bg`, top, mid, bottom)}` +
    `<radialGradient id="${p}ln" cx=".4" cy=".35" r=".7"><stop offset="0" stop-color="${tint(red, 0.3)}"/><stop offset=".6" stop-color="${red}"/><stop offset="1" stop-color="${redDeep}"/></radialGradient>` +
    fade(`${p}gl`, gold, P.dark ? 0.16 : 0.26) +
    fade(`${p}lh`, P.dark ? P.orange : gold, P.dark ? 0.3 : 0.35) +
    `</defs>`;

  /** an auspicious cloud (祥云): three bumps on a base line with curls at the ends, gold outline */
  const cloud = (x: number, y: number, s: number, flip: 1 | -1) => {
    const X = (dx: number) => n(x + dx * s * flip);
    const Y = (dy: number) => n(y + dy * s);
    const d =
      `M${X(-80)} ${Y(0)}C${X(-100)} ${Y(-2)} ${X(-104)} ${Y(-30)} ${X(-82)} ${Y(-32)}C${X(-72)} ${Y(-33)} ${X(-66)} ${Y(-22)} ${X(-74)} ${Y(-16)}` +
      `M${X(-80)} ${Y(0)}H${X(90)}C${X(112)} ${Y(0)} ${X(112)} ${Y(-30)} ${X(88)} ${Y(-30)}C${X(90)} ${Y(-58)} ${X(52)} ${Y(-62)} ${X(40)} ${Y(-40)}` +
      `C${X(34)} ${Y(-80)} ${X(-24)} ${Y(-82)} ${X(-30)} ${Y(-40)}C${X(-40)} ${Y(-58)} ${X(-72)} ${Y(-50)} ${X(-66)} ${Y(-26)}` +
      `M${X(40)} ${Y(-40)}C${X(46)} ${Y(-26)} ${X(30)} ${Y(-18)} ${X(22)} ${Y(-28)}M${X(-30)} ${Y(-40)}C${X(-26)} ${Y(-26)} ${X(-10)} ${Y(-24)} ${X(-6)} ${Y(-34)}`;
    return (
      `<g data-motif="${Math.round(x)}"><path d="${d}H${X(-80)}" fill="${cloudFill}" fill-opacity="${o(0.7, 0.8)}"/>` +
      `<path d="${d}" fill="none" stroke="${gold}" stroke-opacity="${o(0.45, 0.7)}" stroke-width="${n(2.5 * s)}" stroke-linecap="round"/></g>`
    );
  };
  /** a string of firecrackers from (x, y) down: a cord, pairs of red rolls with gold caps turned left and right */
  const string = (x: number, y: number, count: number, s: number) => {
    const len = 15 * s;
    const step = 14 * s;
    // rolls stick out left and right in turn, nearly level (tilted down 18°), a gold cap at the outer end
    const dx = Math.cos((18 * Math.PI) / 180);
    const dy = Math.sin((18 * Math.PI) / 180);
    let rolls = '';
    let caps = '';
    for (let i = 0; i < count * 2; i++) {
      const side = i % 2 ? 1 : -1;
      const cy = y + 22 * s + i * step;
      const ex = x + side * dx * len;
      const ey = cy + dy * len;
      rolls += `M${n(x)} ${n(cy)}L${n(ex)} ${n(ey)}`;
      caps += `M${n(ex - side * dx * 3 * s)} ${n(ey - dy * 3 * s)}L${n(ex)} ${n(ey)}`;
    }
    const end = y + 22 * s + count * 2 * step;
    return (
      `<g data-motif="${Math.round(x)}"><path d="M${x} ${y}V${n(end)}" stroke="${cord}" stroke-width="${n(2 * s)}"/>` +
      `<path d="${rolls}" stroke="${red}" stroke-width="${n(9 * s)}"/>` +
      `<path d="${caps}" stroke="${gold}" stroke-width="${n(9 * s)}"/>` +
      `<circle cx="${x}" cy="${y}" r="${n(6 * s)}" fill="${gold}"/>` +
      `<path d="M${x} ${n(end)}V${n(end + 12 * s)}M${n(x - 4 * s)} ${n(end + 12 * s)}L${n(x - 5 * s)} ${n(end + 32 * s)}M${x} ${n(end + 12 * s)}V${n(end + 34 * s)}M${n(x + 4 * s)} ${n(end + 12 * s)}L${n(x + 5 * s)} ${n(end + 32 * s)}" stroke="${red}" stroke-width="${n(2 * s)}" stroke-linecap="round"/></g>`
    );
  };
  /** a red lantern hanging from (x, y): cord, glow, a round body with ribs, gold caps and a gold tassel */
  const lantern = (x: number, y: number, r: number) => {
    const cy = y + r * 1.6;
    return (
      `<g data-motif="${Math.round(x)}"><path d="M${x} ${y}V${n(cy - r)}" stroke="${cord}" stroke-width="2"/>` +
      `<circle cx="${x}" cy="${n(cy)}" r="${n(r * 2.4)}" fill="url(#${p}lh)"/>` +
      `<ellipse cx="${x}" cy="${n(cy)}" rx="${n(r * 1.15)}" ry="${n(r)}" fill="url(#${p}ln)"/>` +
      `<path d="M${x} ${n(cy - r)}V${n(cy + r)}M${n(x - r * 0.55)} ${n(cy - r * 0.86)}Q${n(x - r * 0.8)} ${n(cy)} ${n(x - r * 0.55)} ${n(cy + r * 0.86)}M${n(x + r * 0.55)} ${n(cy - r * 0.86)}Q${n(x + r * 0.8)} ${n(cy)} ${n(x + r * 0.55)} ${n(cy + r * 0.86)}" fill="none" stroke="${redDeep}" stroke-opacity=".5" stroke-width="1.5"/>` +
      `<rect x="${n(x - r * 0.5)}" y="${n(cy - r - 5)}" width="${n(r)}" height="8" rx="2" fill="${gold}"/>` +
      `<rect x="${n(x - r * 0.5)}" y="${n(cy + r - 3)}" width="${n(r)}" height="8" rx="2" fill="${gold}"/>` +
      `<path d="M${x} ${n(cy + r + 5)}V${n(cy + r + 14)}M${n(x - 4)} ${n(cy + r + 14)}L${n(x - 5)} ${n(cy + r + 34)}M${x} ${n(cy + r + 14)}V${n(cy + r + 36)}M${n(x + 4)} ${n(cy + r + 14)}L${n(x + 5)} ${n(cy + r + 34)}" stroke="${gold}" stroke-width="2" stroke-linecap="round"/></g>`
    );
  };
  /** gold sparks: small four-point stars and dots, deterministic, inside a box */
  const sparks = (x0: number, y0: number, x1: number, y1: number, count: number, seed: number) => {
    const r = rng(seed);
    let d = '';
    let dots = '';
    for (let i = 0; i < count; i++) {
      const x = x0 + r() * (x1 - x0);
      const y = y0 + r() * (y1 - y0);
      const s = 3 + r() * 5;
      if (i % 3 === 0) dots += `<circle cx="${n(x)}" cy="${n(y)}" r="${n(s * 0.35)}"/>`;
      else
        d += `M${n(x)} ${n(y - s)}Q${n(x)} ${n(y)} ${n(x + s)} ${n(y)}Q${n(x)} ${n(y)} ${n(x)} ${n(y + s)}Q${n(x)} ${n(y)} ${n(x - s)} ${n(y)}Q${n(x)} ${n(y)} ${n(x)} ${n(y - s)}Z`;
    }
    return `<g data-motif="${Math.round((x0 + x1) / 2)}" fill="${gold}" fill-opacity="${o(0.55, 0.7)}"><path d="${d}"/>${dots}</g>`;
  };
  /** spent red paper curls on the ground */
  const curls = (x0: number, y0: number, count: number, seed: number) => {
    const r = rng(seed);
    let s = '';
    for (let i = 0; i < count; i++) {
      const x = x0 + r() * 220;
      const y = y0 + r() * 110;
      s += `<rect x="${n(x)}" y="${n(y)}" width="${n(6 + r() * 6)}" height="${n(3 + r() * 3)}" rx="1.5" transform="rotate(${Math.round(r() * 180)} ${n(x)} ${n(y)})"/>`;
    }
    return `<g data-motif="${Math.round(x0 + 110)}" fill="${red}" fill-opacity="${o(0.6, 0.55)}">${s}</g>`;
  };

  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}bg)"/>` +
    `<ellipse cx="${W / 2}" cy="${n(H * 0.46)}" rx="${n(W * 0.4)}" ry="${n(H * 0.42)}" fill="url(#${p}gl)"/>`;

  if (wide)
    art +=
      cloud(330, 700, 1, 1) +
      cloud(1290, 560, 0.9, -1) +
      string(350, 118, 11, 1.05) +
      string(262, 150, 7, 0.85) +
      lantern(1236, 104, 30) +
      lantern(1330, 104, 24) +
      sparks(214, 110, 450, 600, 16, 3) +
      sparks(1150, 300, 1386, 520, 12, 5) +
      curls(1170, 760, 14, 9);
  else
    art +=
      sparks(0, 40, 18, 620, 7, 3) +
      sparks(382, 40, 400, 620, 7, 5) +
      cloud(24, 690, 0.5, 1) +
      cloud(376, 700, 0.5, -1);

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">` +
    defs +
    art +
    `</svg>`
  );
}

/** Header accent of Firecrackers: a gold cord with a small red roll hanging from it (repeat-x tile, 16 × 8). */
export function firecrackersAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const gold = warm(P);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 8" width="16" height="8">` +
    `<path d="M0 1.5H16" stroke="${gold}" stroke-opacity="${P.dark ? 0.7 : 0.85}"/>` +
    `<rect x="6" y="2" width="4" height="5" rx="1" fill="${P.red}" fill-opacity="${P.dark ? 0.85 : 0.9}"/>` +
    `<rect x="6" y="6" width="4" height="1.2" fill="${gold}"/>` +
    `</svg>`
  );
}

export default defineScene({ game: 'firecrackers', scene: firecrackersScene, accent: firecrackersAccent });
